import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Divider, Icon, Icons } from '../..';
import { ACTIVE_TAB_PARAM } from '../../../constants';
import { useAppSelector, useGetFiltersFromParams, useGetUserPermissions } from '../../../hooks';
import { useGetPracticeProspectsForSelectQuery, useGetTagsQuery } from '../../../services';
import { Roles, SimulationsTab } from '../../../types';
import { isFiltersDirty, parseProspectToOption, parseRequiredSelectOption } from '../../../utils';
import SidebarButtonGroup from '../SidebarButtonGroup';
import SimulationsFilters from './SimulationsFilters';

const FYP_ITEM = {
  id: SimulationsTab.FYP,
  endElement: <Icons icon={Icon.FOR_YOU} />,
};

const FAVORITES_ITEM = {
  id: SimulationsTab.FAVORITES,
  endElement: <Icons icon={Icon.STAR} color="stroke-orange-500" fillColor="fill-orange-500" />,
};

const DRAFTS_ITEM = {
  id: SimulationsTab.DRAFTS,
  endElement: <Icons icon={Icon.DRAFT} />,
};

const LIBRARY_ITEM = {
  id: SimulationsTab.LIBRARY,
  endElement: <Icons icon={Icon.LIBRARY} />,
};

const SimulationsSidebarContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const user = useAppSelector((state) => state.auth.user);

  const { canUserViewLibrary, canUserCreateSims } = useGetUserPermissions();

  const activeTab = searchParams.get(ACTIVE_TAB_PARAM);

  const isSalesRep = user?.role === Roles.SALES_REP;

  const { data: prospects = [], isLoading: isProspectsLoading } = useGetPracticeProspectsForSelectQuery({});
  const { data: tags = [], isLoading: isTagsLoading } = useGetTagsQuery();

  // Get filters from params and check if they are dirty.
  const filters = useGetFiltersFromParams();
  const showFilters = useMemo(() => isFiltersDirty(filters), [filters]);

  const accountOptions = Array.from(new Set(prospects.map((p) => p.accountName))).map(parseRequiredSelectOption);
  const prospectOptions = prospects.map((prospect) => parseProspectToOption(prospect));
  const tagOptions = tags.map(({ id, name, color }) => ({ value: id, label: name, color }));

  const items = useMemo(() => {
    const items = [FAVORITES_ITEM];

    if (isSalesRep) {
      // Sales reps get FYP at the beginning.
      items.unshift(FYP_ITEM);
    }

    if (canUserCreateSims) {
      items.push(DRAFTS_ITEM);
    }

    if (canUserViewLibrary) {
      if (isSalesRep) {
        items.push(LIBRARY_ITEM);
      } else {
        // Non-sales reps get library at the beginning.
        items.unshift(LIBRARY_ITEM);
      }
    }

    return items;
  }, [isSalesRep, canUserViewLibrary, canUserCreateSims]);

  return (
    <>
      <SidebarButtonGroup
        items={items.map((item) => ({
          ...item,
          active: item.id === activeTab,
          onClick: () => setSearchParams({ [ACTIVE_TAB_PARAM]: item.id }),
        }))}
      />
      {showFilters && (
        <>
          <Divider />
          <SimulationsFilters
            filters={filters}
            accountOptions={accountOptions}
            prospectOptions={prospectOptions}
            tagOptions={tagOptions}
            isProspectsLoading={isProspectsLoading}
            isTagsLoading={isTagsLoading}
          />
        </>
      )}
    </>
  );
};

export default SimulationsSidebarContent;
